import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { cartItemKey, getItemSelections, productDetailLink } from '../utils/productLinks';
import { getSelectionImages } from '../utils/productSelection';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

// Threshold mirrors backend FREE_SHIPPING_THRESHOLD in app/api/checkout.py.
// TODO: fetch from a /checkout/config endpoint so the two stay in sync.
const FREE_SHIPPING_THRESHOLD = 50;

function getItemKey(item, index) {
  return `${cartItemKey(item)}-${index}`;
}

function getDisplayName(item, product) {
  let name = item.product_name || product?.name || 'Unknown Product';
  if (item.variant_name) {
    name += ` — ${item.variant_name}`;
  } else if (item.variant_id && product?.variants) {
    const v = product.variants.find((x) => x.id === item.variant_id);
    if (v) name += ` — ${v.name}`;
  }
  return name;
}

function getItemImage(item, product) {
  return getSelectionImages(product, item.variant_id, getItemSelections(item))[0] || null;
}

export default function MiniCart() {
  const navigate = useNavigate();
  const {
    cart,
    isMiniCartOpen,
    closeMiniCart,
    updateCartItem,
    removeFromCart,
    getCartTotal,
    getCartItemCount,
  } = useCart();

  const [products, setProducts] = useState({});
  // Track ids that 404'd or errored so we never re-request them. Without
  // this, an orphan cart item (product was deleted) would put the effect
  // into a permanent re-fetch loop because `products` keeps getting a new
  // reference each render, retriggering the effect.
  const [failedIds, setFailedIds] = useState(() => new Set());
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [busy, setBusy] = useState({});

  const itemCount = getCartItemCount();
  const subtotal = getCartTotal();
  const hasVolumeSavings = (cart.items || []).some(item => {
    const product = products[item.product_id];
    const variant = product?.variants?.find(option => option.id === item.variant_id);
    return Boolean(product?.quantity_discounts?.length || variant?.quantity_discounts?.length);
  });

  const fetchProductDetails = useCallback(async (ids) => {
    if (!ids.length) return;
    setLoadingProducts(true);
    try {
      const responses = await Promise.all(
        ids.map((id) =>
          axios
            .get(`${API_BASE_URL}/products/${id}`)
            .then((r) => ({ id, ok: true, data: r.data }))
            .catch(() => ({ id, ok: false }))
        )
      );
      const fetched = {};
      const failed = [];
      responses.forEach((r) => {
        if (r.ok) fetched[r.data.id] = r.data;
        else failed.push(r.id);
      });
      if (Object.keys(fetched).length) {
        setProducts((prev) => ({ ...prev, ...fetched }));
      }
      if (failed.length) {
        setFailedIds((prev) => {
          const next = new Set(prev);
          failed.forEach((id) => next.add(id));
          return next;
        });
      }
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // Lazy-load product details only when the drawer is actually open and we
  // don't already have them cached or known to be missing.
  useEffect(() => {
    if (!isMiniCartOpen || !cart.items?.length) return;
    const missing = [...new Set(cart.items.map((i) => i.product_id))].filter(
      (id) => !products[id] && !failedIds.has(id)
    );
    if (missing.length) fetchProductDetails(missing);
  }, [isMiniCartOpen, cart.items, products, failedIds, fetchProductDetails]);

  // Body scroll lock + Esc to close.
  useEffect(() => {
    if (!isMiniCartOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') closeMiniCart();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isMiniCartOpen, closeMiniCart]);

  const onChangeQty = async (item, delta, key) => {
    const next = item.quantity + delta;
    if (next < 1) return;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await updateCartItem(item.product_id, next, item.variant_id, getItemSelections(item));
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const onRemove = async (item, key) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await removeFromCart(item.product_id, item.variant_id, getItemSelections(item));
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const onCheckout = () => {
    closeMiniCart();
    navigate('/checkout');
  };

  const onViewCart = () => {
    closeMiniCart();
    navigate('/cart');
  };

  const remainingForFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const freeShippingProgress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
  const qualifiesForFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-50 transition-opacity duration-300 ${
          isMiniCartOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMiniCart}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${
          isMiniCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Shopping cart"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            Your Cart {itemCount > 0 && <span className="text-gray-400 font-normal">({itemCount})</span>}
          </h2>
          <button
            onClick={closeMiniCart}
            className="p-2 -mr-2 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded-full transition-colors"
            aria-label="Close cart"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Empty state */}
        {(!cart.items || cart.items.length === 0) ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                <path d="M3 6h18" /><path d="M16 10a4 4 0 01-8 0" />
              </svg>
            </div>
            <p className="text-base font-medium text-gray-900 mb-1">Your cart is empty</p>
            <p className="text-sm text-gray-500 mb-6">Add items to get started.</p>
            <Link
              to="/products"
              onClick={closeMiniCart}
              className="inline-flex items-center justify-center px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors"
            >
              Continue shopping
            </Link>
          </div>
        ) : (
          <>
            {/* Free shipping progress */}
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              {qualifiesForFreeShipping ? (
                <p className="text-sm font-medium text-emerald-700 flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  You've unlocked free shipping
                </p>
              ) : (
                <p className="text-xs text-gray-700 mb-1.5">
                  Add <span className="font-semibold">${remainingForFreeShipping.toFixed(2)}</span> more for free shipping
                </p>
              )}
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${freeShippingProgress}%` }}
                />
              </div>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {cart.items.map((item, index) => {
                const product = products[item.product_id];
                const key = getItemKey(item, index);
                const image = getItemImage(item, product);
                const components = item.component_selections || [];
                const isBusy = busy[key];

                return (
                  <div key={key} className="flex gap-3">
                    {/* Thumbnail */}
                    <Link
                      to={productDetailLink(item.product_id, item.variant_id, getItemSelections(item))}
                      onClick={closeMiniCart}
                      className="flex-shrink-0"
                    >
                      {image ? (
                        <img
                          src={image}
                          alt={item.product_name || ''}
                          loading="lazy"
                          className="w-20 h-20 object-cover rounded-lg bg-gray-100"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded-lg" />
                      )}
                    </Link>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={productDetailLink(item.product_id, item.variant_id, getItemSelections(item))}
                        onClick={closeMiniCart}
                        className="block text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2"
                      >
                        {getDisplayName(item, product)}
                      </Link>
                      {components.length > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {components.map((c) => `${c.component_type_name}: ${c.value_name}`).join(' · ')}
                        </p>
                      )}

                      <div className="mt-2 flex items-center justify-between gap-2">
                        {/* Qty stepper */}
                        <div className="flex items-center border border-gray-200 rounded-full">
                          <button
                            onClick={() => onChangeQty(item, -1, key)}
                            disabled={isBusy || item.quantity <= 1}
                            className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                            aria-label="Decrease quantity"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                          </button>
                          <span className="w-7 text-center text-sm font-medium text-gray-900 select-none">{item.quantity}</span>
                          <button
                            onClick={() => onChangeQty(item, 1, key)}
                            disabled={isBusy}
                            className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                            aria-label="Increase quantity"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                          </button>
                        </div>

                        <span className="text-sm font-semibold text-gray-900">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>

                      <button
                        onClick={() => onRemove(item, key)}
                        disabled={isBusy}
                        className="mt-1.5 text-xs text-gray-400 hover:text-red-600 disabled:opacity-50 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {loadingProducts && (
                <p className="text-xs text-center text-gray-400 py-2">Loading product details…</p>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-5 py-4 space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-gray-600">{hasVolumeSavings ? 'Subtotal before volume savings' : 'Subtotal'}</span>
                <span className="text-lg font-bold text-gray-900">${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500">Tax and shipping calculated at checkout.</p>
              {hasVolumeSavings && <p className="text-xs text-gray-500">Volume savings are applied in your full cart and at checkout.</p>}

              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  onClick={onViewCart}
                  className="py-3 text-sm font-semibold text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                >
                  View cart
                </button>
                <button
                  onClick={onCheckout}
                  className="py-3 text-sm font-semibold text-white bg-primary rounded-full hover:bg-primary-dark transition-colors"
                >
                  Checkout
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
